/*
 * An approach to PubSub that uses native CustomEvents, adapted from:
 * https://levelup.gitconnected.com/simple-pub-sub-pattern-using-windows-custom-event-219b2883069
 * Code sample: https://codesandbox.io/s/holy-water-yo3h4s?from-embed
 *
 * This is a dependency-free PubSub pattern that supports subscribe, unsubscribe, and publish (fire an event). It keeps
 * track of each subscriber by generating a unique ID for each one and storing it in a subscribers Map. Storing
 * subscribers in a data structure centralizes this functionality within an app, and using a Map over an Array makes
 * unsubscribing slightly more efficient and concise.
 *
 * An approach that makes use of native custom events is advantageous bc it allows for subscribers to respond to events
 * that are fired both from the theme and from external sources, such as DY or other vendors. Ideally, subscribers will
 * always be registered to a pubSub instance and events will be published via the same instance. However, given the
 * number of vendors and third-party tech we interact with, it is very likely that we will need to respond to events
 * that do not originate from within the theme, and potentially even from sources that we do not have control over. With
 * this approach, we may not always know where the events are coming from, but we can always know where we are listening
 * for the event and how the app will respond to the event (the callback).
 *
 * Importing:
 * import { pubSub } from '@utils/PubSub';
 *
 * Subscribing (callback is a functionn that will run when the event fires):
 * pubSub.subscribe('event:name', callback);
 *
 * Unsubscribing (callback must match the one set with subscribe):
 * pubSub.unsubscribe('event:name', callback);
 *
 * Running multiple callbacks when subscribing to the same event:
 * pubSub.subscribe('event:name', firstCallback);
 * pubSub.subscribe('event:name', secondCallback);
 *
 * Publishing looks very similar to dispatchEvent. The detail object (second argument) is optional:
 * document.pubSub.publish('event:name');
 * document.pubSub.publish('event:name', {triggered: true});
 */
export const pubSub = {
  broadcast() {
    window.Brooklinen.pubSubReady = true;
  },
  subscribers: new Map(),
  subscribe(event, callback, fallback = false) {
    // Subscribe to an event and repsond to it.
    const id = `${event}__${Date.now()}__${Math.floor(Math.random() * 1000)}`;
    this.subscribers.set(id, { event, callback });

    document.addEventListener(event, callback);
    if (fallback) {
      const expiration = window.Brooklinen.dyExpiration || 5000;

      setTimeout(() => {
        const subscription = this.subscribers.get(id);
        const hasFired = subscription === undefined;
        if (hasFired) return;

        this.unsubscribe(event, callback);
        fallback();
      }, expiration);
    }
  },
  unsubscribe(event, callback) {
    // Unsubscribe a single subscriber.
    for (let [id, data] of this.subscribers) {
      if (data.event == event && data.callback == callback) {
        document.removeEventListener(event, callback);
        this.subscribers.delete(id);
      }
    }
  },
  publish(event, detail = { triggered: true }) {
    document.dispatchEvent(new CustomEvent(event, { detail }));
  },
};
